import { useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import api from "@/lib/api";
import { formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import {
  Rocket, Sparkles, ScanSearch, ClipboardCheck, Ship, ArrowRight, Loader2,
  ShieldAlert, CheckCircle2, ExternalLink, Boxes, MailCheck,
} from "lucide-react";

const AVATAR = "https://static.prod-images.emergentagent.com/jobs/8e07b049-932d-479b-9784-acbfa0bcc283/images/22a10a1f6f9e4095b10076cb2eba7c8accbefffd2ffcb3d6391a469eb7af00ac.jpeg";

const TABS = [
  { id: "classify", label: "Instant Classification", icon: ScanSearch },
  { id: "healthcheck", label: "HS Health Check", icon: ClipboardCheck },
  { id: "clearance", label: "Clearance Request (SI / HR)", icon: Ship },
];

export default function Landing() {
  const [tab, setTab] = useState("classify");

  return (
    <div className="min-h-screen bg-[#09090B] text-zinc-100 font-sans">
      {/* Nav */}
      <nav className="sticky top-0 z-30 border-b border-zinc-800/80 bg-[#09090B]/90 backdrop-blur-md">
        <div className="max-w-6xl mx-auto px-6 h-14 flex items-center gap-2">
          <Rocket className="h-5 w-5 text-blue-500" />
          <span className="font-heading font-bold tracking-tight">ROKET</span>
          <span className="text-[10px] font-mono text-zinc-600 uppercase ml-1 hidden sm:inline">Customs Colleague</span>
          <div className="ml-auto flex items-center gap-3">
            <a href="#try" className="text-sm text-zinc-400 hover:text-white transition-colors duration-150 hidden sm:inline">Try Roket</a>
            <a href="https://www.europacific.com" target="_blank" rel="noreferrer" className="text-sm text-zinc-400 hover:text-white transition-colors duration-150 hidden sm:inline">Europacific</a>
            <Link to="/app" data-testid="open-console-btn" className="text-sm bg-zinc-800 hover:bg-zinc-700 px-3 py-1.5 rounded-sm transition-colors duration-150">Open console</Link>
          </div>
        </div>
      </nav>

      {/* Hero */}
      <header className="relative overflow-hidden border-b border-zinc-800">
        <div className="absolute inset-0 opacity-[0.04]" style={{ backgroundImage: "radial-gradient(circle at 1px 1px, #fff 1px, transparent 0)", backgroundSize: "28px 28px" }} />
        <div className="max-w-6xl mx-auto px-6 py-16 grid lg:grid-cols-2 gap-10 items-center relative">
          <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
            <div className="inline-flex items-center gap-2 text-xs font-mono text-blue-400 border border-blue-500/30 bg-blue-500/10 rounded-sm px-2.5 py-1 mb-5">
              <Sparkles className="h-3.5 w-3.5" /> New teammate on the customs desk
            </div>
            <h1 className="font-heading text-4xl sm:text-5xl font-semibold tracking-tight leading-[1.05]">
              Meet <span className="text-blue-500">Roket</span> — your AI customs colleague
            </h1>
            <p className="mt-5 text-zinc-400 text-base max-w-lg leading-relaxed">
              Roket sits next to your team and handles the heavy lifting of EU customs: classifying cargo,
              double-checking HS codes for compliance risk, and preparing declarations for ZZI eCustoms —
              with a human always in the loop for the final call.
            </p>
            <div className="mt-7 flex flex-wrap gap-3">
              <a href="#try" data-testid="hero-try-btn" className="flex items-center gap-2 bg-blue-600 hover:bg-blue-500 text-white text-sm px-4 py-2.5 rounded-sm transition-colors duration-150">
                Try Roket now <ArrowRight className="h-4 w-4" />
              </a>
              <a href="#clearance" onClick={() => setTab("clearance")} className="flex items-center gap-2 border border-zinc-700 hover:bg-zinc-900 text-sm px-4 py-2.5 rounded-sm transition-colors duration-150">
                Request clearance (SI / HR)
              </a>
            </div>
          </motion.div>
          <motion.div initial={{ opacity: 0, scale: 0.94 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.5, delay: 0.15 }} className="justify-self-center">
            <div className="relative">
              <div className="absolute -inset-4 bg-blue-500/10 blur-2xl rounded-full" />
              <img src={AVATAR} alt="Roket" className="relative w-64 h-64 rounded-sm border border-zinc-800 object-cover" />
            </div>
          </motion.div>
        </div>
      </header>

      {/* What Roket does */}
      <section className="max-w-6xl mx-auto px-6 py-14">
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {[
            { icon: ScanSearch, t: "Classifies cargo", d: "Reads a product description and returns the correct HS code with GRI reasoning, duty and compliance flags." },
            { icon: ClipboardCheck, t: "Health-checks HS codes", d: "Reviews your declared classifications line-by-line and flags risk — inspired by Maersk's health check." },
            { icon: Boxes, t: "Prepares declarations", d: "Builds ZZI eCustoms EUL XML (IE415B / T1 / IE515) ready for submission after approval." },
            { icon: MailCheck, t: "Routes clearance", d: "Forwards Slovenia & Croatia clearance requests straight to the Europacific customs team." },
          ].map((c, i) => (
            <motion.div key={c.t} initial={{ opacity: 0, y: 10 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.05 }}
              className="border border-zinc-800 rounded-sm bg-zinc-900/40 p-4 hover:border-zinc-700 transition-colors duration-150">
              <c.icon className="h-5 w-5 text-blue-500 mb-3" />
              <h3 className="font-heading font-semibold text-sm mb-1">{c.t}</h3>
              <p className="text-xs text-zinc-500 leading-relaxed">{c.d}</p>
            </motion.div>
          ))}
        </div>
      </section>

      {/* Interactive tools */}
      <section id="try" className="max-w-4xl mx-auto px-6 pb-20 scroll-mt-16">
        <div className="text-center mb-6">
          <h2 className="font-heading text-2xl font-semibold tracking-tight">Try Roket live</h2>
          <p className="text-sm text-zinc-500 mt-1">No account needed — run a real classification, health check, or clearance request.</p>
        </div>

        <div className="flex flex-wrap gap-2 mb-4 justify-center">
          {TABS.map((t) => (
            <button key={t.id} data-testid={`tab-${t.id}`} onClick={() => setTab(t.id)}
              className={`flex items-center gap-2 text-sm px-3 py-2 rounded-sm border transition-colors duration-150 ${tab === t.id ? "border-blue-500 bg-blue-500/10 text-blue-300" : "border-zinc-800 text-zinc-400 hover:bg-zinc-900"}`}>
              <t.icon className="h-4 w-4" /> {t.label}
            </button>
          ))}
        </div>

        <div id="clearance" className="border border-zinc-800 rounded-sm bg-zinc-900/40 p-5 scroll-mt-20">
          {tab === "classify" && <ClassifyForm />}
          {tab === "healthcheck" && <HealthCheckForm />}
          {tab === "clearance" && <ClearanceForm />}
        </div>
      </section>

      <footer className="border-t border-zinc-800">
        <div className="max-w-6xl mx-auto px-6 py-8 flex flex-wrap items-center gap-3 text-sm text-zinc-500">
          <Rocket className="h-4 w-4 text-blue-500" />
          <span className="font-heading text-zinc-300">ROKET</span>
          <span className="text-zinc-600">· EU customs automation for</span>
          <a href="https://www.europacific.com" target="_blank" rel="noreferrer" className="text-blue-400 hover:underline inline-flex items-center gap-1">
            Europacific <ExternalLink className="h-3 w-3" />
          </a>
          <span className="ml-auto font-mono text-xs text-zinc-600">Clearance desk: customs@europacific.com</span>
        </div>
      </footer>
    </div>
  );
}

function Label({ children }) {
  return <label className="block text-[11px] uppercase tracking-wider text-zinc-500 mb-1">{children}</label>;
}
const inputCls = "w-full bg-zinc-950 border border-zinc-800 rounded-sm px-3 py-2 text-sm focus:border-blue-500 outline-none";

function ClassifyForm() {
  const [desc, setDesc] = useState("Wireless bluetooth over-ear headphones with active noise cancelling");
  const [email, setEmail] = useState("");
  const [res, setRes] = useState(null);
  const [loading, setLoading] = useState(false);
  const run = async () => {
    if (!desc.trim()) return;
    if (!/^\S+@\S+\.\S+$/.test(email)) { toast.error("Enter a valid email to receive your result"); return; }
    setLoading(true); setRes(null);
    try { setRes((await api.post("/public/classify", { description: desc, email })).data); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Classification failed, try again"); }
    finally { setLoading(false); }
  };
  return (
    <div>
      <Label>Describe your cargo</Label>
      <textarea data-testid="landing-classify-input" rows={3} value={desc} onChange={(e) => setDesc(e.target.value)} className={`${inputCls} font-mono text-xs resize-none mb-3`} />
      <Label>Your email — we'll send the full result to your inbox</Label>
      <input data-testid="landing-classify-email" type="email" placeholder="you@company.com" value={email} onChange={(e) => setEmail(e.target.value)} className={`${inputCls} mb-3`} />
      <button data-testid="landing-classify-btn" onClick={run} disabled={loading} className="flex items-center gap-2 bg-blue-600 hover:bg-blue-500 disabled:opacity-60 text-white text-sm px-4 py-2 rounded-sm">
        {loading && <Loader2 className="h-4 w-4 animate-spin" />} Classify & email me the result
      </button>
      {res && (
        <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} className="mt-5 border-t border-zinc-800 pt-4" data-testid="landing-classify-result">
          <div className="flex items-baseline gap-3 flex-wrap">
            <span className="font-mono text-2xl text-white">{res.hs_code || "—"}</span>
            {res.confidence != null && <span className="text-[10px] font-mono px-1.5 py-0.5 rounded-sm border border-blue-500/40 text-blue-400">{res.confidence}%</span>}
            {res.gri_rule && <span className="text-[10px] font-mono text-zinc-500">{res.gri_rule}</span>}
            {res.duty?.mfn_duty_pct != null && <span className="text-xs font-mono text-zinc-300">MFN {res.duty.mfn_duty_pct}%</span>}
          </div>
          <p className="text-xs text-zinc-500 mt-1">{res.description}</p>
          <p className="text-sm text-zinc-300 mt-2 leading-relaxed">{res.reasoning}</p>
          {res.compliance?.length > 0 && (
            <div className="flex flex-wrap gap-1.5 mt-3">
              {res.compliance.map((c, i) => (
                <span key={i} className={`inline-flex items-center gap-1 text-[10px] font-mono px-2 py-0.5 rounded-sm border ${c.severity === "high" ? "border-red-500/40 text-red-400" : c.severity === "medium" ? "border-amber-500/40 text-amber-400" : "border-zinc-700 text-zinc-400"}`}>
                  <ShieldAlert className="h-3 w-3" /> {c.regime}
                </span>
              ))}
            </div>
          )}
          {res.emailed && <p className="text-[11px] text-emerald-400 mt-3 inline-flex items-center gap-1"><MailCheck className="h-3 w-3" /> Full result emailed to {res.email}</p>}
          <p className="text-[10px] text-zinc-600 mt-2">Indicative result from Roket's TARIC dataset. A specialist confirms before any declaration.</p>
        </motion.div>
      )}
    </div>
  );
}

function HealthCheckForm() {
  const SAMPLE = "Portable laptop 1.2kg | 8471300000\nWireless headphones | 8517620000\nMen's cotton t-shirts | 6109100010";
  const [text, setText] = useState(SAMPLE);
  const [email, setEmail] = useState("");
  const [res, setRes] = useState(null);
  const [loading, setLoading] = useState(false);
  const run = async () => {
    if (!/^\S+@\S+\.\S+$/.test(email)) { toast.error("Enter a valid email to receive your report"); return; }
    const lines = text.split("\n").map((l) => l.trim()).filter(Boolean).map((l) => {
      const i = l.lastIndexOf("|");
      return i === -1 ? { description: l, declared_hs_code: "" } : { description: l.slice(0, i).trim(), declared_hs_code: l.slice(i + 1).trim() };
    });
    setLoading(true); setRes(null);
    try { setRes((await api.post("/public/health-check", { lines, email })).data); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Health check failed"); }
    finally { setLoading(false); }
  };
  const VC = { valid: "text-emerald-400 border-emerald-500/40", review: "text-amber-400 border-amber-500/40", incorrect: "text-red-400 border-red-500/40" };
  return (
    <div>
      <Label>Your declared classifications — one per line: <span className="font-mono">description | HS code</span></Label>
      <textarea data-testid="landing-hc-input" rows={4} value={text} onChange={(e) => setText(e.target.value)} className={`${inputCls} font-mono text-xs resize-none mb-3`} />
      <Label>Your email — we'll send the full confidential report</Label>
      <input data-testid="landing-hc-email" type="email" placeholder="you@company.com" value={email} onChange={(e) => setEmail(e.target.value)} className={`${inputCls} mb-3`} />
      <button data-testid="landing-hc-btn" onClick={run} disabled={loading} className="flex items-center gap-2 bg-blue-600 hover:bg-blue-500 disabled:opacity-60 text-white text-sm px-4 py-2 rounded-sm">
        {loading && <Loader2 className="h-4 w-4 animate-spin" />} Run health check & email me
      </button>
      {res && (
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="mt-5 border-t border-zinc-800 pt-4" data-testid="landing-hc-result">
          <p className="text-xs font-mono text-zinc-400 mb-3">
            {res.summary.total} lines · <span className="text-emerald-400">{res.summary.valid} valid</span> · <span className="text-amber-400">{res.summary.review} review</span> · <span className="text-red-400">{res.summary.incorrect} incorrect</span>
          </p>
          <div className="space-y-2">
            {res.lines.map((ln) => (
              <div key={ln.line_id} className="flex items-center gap-3 text-xs border border-zinc-800 rounded-sm p-2">
                <span className="flex-1 text-zinc-300 truncate">{ln.description}</span>
                <span className="font-mono text-zinc-500">{ln.declared_hs_code || "—"}</span>
                <ArrowRight className="h-3 w-3 text-zinc-600" />
                <span className="font-mono text-blue-400">{ln.recommended_hs_code || "—"}</span>
                <span className={`px-1.5 py-0.5 rounded-sm border text-[10px] font-mono ${VC[ln.verdict] || "border-zinc-700 text-zinc-400"}`}>{(ln.verdict || "—").toUpperCase()}</span>
              </div>
            ))}
          </div>
          {res.emailed && <p className="text-[11px] text-emerald-400 mt-3 inline-flex items-center gap-1"><MailCheck className="h-3 w-3" /> Full report emailed to {res.email}</p>}
          <p className="text-[10px] text-zinc-600 mt-2">Want the full confidential report on up to 25 codes? A Roket specialist follows up.</p>
        </motion.div>
      )}
    </div>
  );
}

function ClearanceForm() {
  const [form, setForm] = useState({ name: "", email: "", company: "", country: "Slovenia", origin: "", cargo: "", details: "" });
  const [done, setDone] = useState(null);
  const [loading, setLoading] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try { setDone((await api.post("/public/clearance-request", form)).data); toast.success("Request forwarded to Europacific customs"); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not submit — check the form"); }
    finally { setLoading(false); }
  };
  if (done) return (
    <div className="text-center py-6" data-testid="landing-clearance-success">
      <CheckCircle2 className="h-10 w-10 text-emerald-400 mx-auto mb-3" />
      <p className="text-sm text-emerald-300 font-medium">Request <span className="font-mono">{done.ref}</span> received</p>
      <p className="text-xs text-zinc-400 mt-2">Forwarded to <span className="font-mono text-zinc-300">{done.forwarded_to}</span>. The Europacific customs team will be in touch.</p>
    </div>
  );
  return (
    <form onSubmit={submit} data-testid="landing-clearance-form">
      <p className="text-xs text-zinc-500 mb-4">Customs clearance for <b className="text-zinc-300">Slovenia</b> & <b className="text-zinc-300">Croatia</b> is handled by Europacific — this form goes straight to their customs desk.</p>
      <div className="grid sm:grid-cols-2 gap-3">
        <div><Label>Name</Label><input data-testid="clearance-name" required value={form.name} onChange={set("name")} className={inputCls} /></div>
        <div><Label>Email</Label><input data-testid="clearance-email" type="email" required value={form.email} onChange={set("email")} className={inputCls} /></div>
        <div><Label>Company</Label><input data-testid="clearance-company" value={form.company} onChange={set("company")} className={inputCls} /></div>
        <div><Label>Country</Label>
          <select data-testid="clearance-country" value={form.country} onChange={set("country")} className={inputCls}>
            <option>Slovenia</option><option>Croatia</option>
          </select>
        </div>
        <div><Label>Origin of goods</Label><input data-testid="clearance-origin" value={form.origin} onChange={set("origin")} className={inputCls} /></div>
        <div><Label>Cargo</Label><input data-testid="clearance-cargo" required value={form.cargo} onChange={set("cargo")} className={inputCls} /></div>
      </div>
      <div className="mt-3"><Label>Details (optional)</Label><textarea data-testid="clearance-details" rows={2} value={form.details} onChange={set("details")} className={`${inputCls} resize-none`} /></div>
      <button data-testid="clearance-submit" disabled={loading} className="mt-4 flex items-center gap-2 bg-blue-600 hover:bg-blue-500 disabled:opacity-60 text-white text-sm px-4 py-2 rounded-sm">
        {loading && <Loader2 className="h-4 w-4 animate-spin" />} Send to Europacific customs
      </button>
    </form>
  );
}
